// G1 (ADR-020, docs/PLAN-GOOGLE-MAPS.md): referencia de lugar del pedido y
// busqueda de direcciones por el proxy de la API. La app nunca habla con
// Google ni recibe coordenadas de Google: busca por la API y manda el placeId
// elegido; la API resuelve las coordenadas dentro del alta del pedido.
import { z } from "zod";
import { Decimal } from "./common.js";
export const MOBILE_PLACE_LABEL_MAX = 120;
export const PLACE_AUTOCOMPLETE_INPUT_MIN = 3;
export const PLACE_AUTOCOMPLETE_INPUT_MAX = 120;
export const PLACE_SUGGESTIONS_MAX = 5;
// zod-to-openapi 9 publica los flags del regex dentro de pattern ("...$/iu"),
// que ya no es un patron valido para el cliente generado: se publica el source.
const PLACE_SESSION_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const GOOGLE_PLACE_ID_PATTERN = /^[A-Za-z0-9_-]+$/u;
export const PlaceSessionToken = z
    .string()
    .regex(PLACE_SESSION_TOKEN_PATTERN)
    .openapi("PlaceSessionToken", {
    pattern: PLACE_SESSION_TOKEN_PATTERN.source,
    description: "Token de sesion de busqueda: UUID v4 que genera la app al empezar a escribir en un campo y repite en cada busqueda y en el alta del pedido. Opaco para Google y para la API; no identifica a nadie.",
    example: "3f2b8c1e-7a4d-4e1b-9c2f-5a6b7c8d9e0f",
});
export const GooglePlaceId = z
    .string()
    .min(1)
    .max(512)
    .regex(GOOGLE_PLACE_ID_PATTERN)
    .openapi("GooglePlaceId", {
    pattern: GOOGLE_PLACE_ID_PATTERN.source,
    description: "Identificador de lugar de Google (place ID). Se puede guardar sin vencimiento; las coordenadas y la etiqueta, no.",
    example: "ChIJN1t_tDeuEmsRUsoyG83frY4",
});
// --- PlaceRef: el lugar de un extremo del pedido ------------------------------
export const MobileHomePlace = z.object({ type: z.literal("home") }).strict()
    .openapi("MobileHomePlace", { description: "El domicilio del legajo del pasajero, tal como lo tiene la planta. No viaja por el wire." });
export const SitePlace = z.object({
    type: z.literal("site"),
    gateId: z.string().min(1).max(128).optional().openapi({ description: "Porteria de la planta; ausente es la porteria principal." }),
}).strict().openapi("SitePlace", {
    description: "La planta del path, con porteria opcional. Dato de la empresa, no pasa por Google. Como 'place' (forma anterior) es 400 validation: el otro extremo ya es la planta. Como origin o destination (forma nueva, 0.6.22) define el sentido.",
});
export const GooglePlaceRef = z.object({
    type: z.literal("google_place"),
    placeId: GooglePlaceId,
    sessionToken: PlaceSessionToken,
    label: z.string().trim().min(1).max(MOBILE_PLACE_LABEL_MAX).openapi({
        description: "Texto principal de la sugerencia que toco el pasajero (hasta 120). Se guarda como etiqueta del pedido con el mismo vencimiento que las coordenadas (30 dias). Solo lo ve el propio pasajero.",
    }),
}).strict().openapi("GooglePlaceRef", {
    description: "Lugar elegido de la busqueda. La API lo resuelve a coordenadas con Google dentro del alta y cierra la sesion de busqueda; el cliente nunca manda ni recibe coordenadas de Google. Solo para auto: con combi es 422 place_not_allowed_for_service.",
});
export const MobilePointPlace = z.object({
    type: z.literal("point"),
    label: z.string().trim().min(1).max(MOBILE_PLACE_LABEL_MAX).openapi({ description: "Como lo nombra el pasajero (hasta 120). Solo lo ve el propio pasajero; no sale a torre, consola ni proveedor." }),
    lat: Decimal.min(-90).max(90).openapi({ description: "Latitud WGS84.", example: -38.7183 }),
    lng: Decimal.min(-180).max(180).openapi({ description: "Longitud WGS84.", example: -62.2663 }),
}).strict().openapi("MobilePointPlace", {
    description: "Punto libre con etiqueta y coordenadas (PB-4c). Queda para fixtures, pruebas e importacion: en staging y prod la app lo recibe rechazado (400 validation) cuando se prende el corte de G3. Tiene que caer dentro del area de operacion de la planta cuando esta declarada.",
});
export const PlaceRef = z.discriminatedUnion("type", [MobileHomePlace, SitePlace, GooglePlaceRef, MobilePointPlace]).openapi("PlaceRef", {
    description: "Referencia a un lugar: domicilio del legajo (home), la planta (site), un lugar de la busqueda de Google (google_place) o un punto libre (point).",
});
// --- Busqueda (proxy de Places Autocomplete) ----------------------------------
export const PlaceAutocompleteRequest = z.object({
    input: z.string().trim().min(PLACE_AUTOCOMPLETE_INPUT_MIN).max(PLACE_AUTOCOMPLETE_INPUT_MAX).openapi({
        description: "Texto tipeado, de 3 a 120 caracteres. La API no lo guarda ni lo loguea.",
    }),
    sessionToken: PlaceSessionToken,
}).strict().openapi("PlaceAutocompleteRequest", {
    description: "Busqueda de direcciones. El sesgo geografico (sede de la planta del path) lo decide la API; el cliente no manda ubicacion.",
});
export const PlaceSuggestion = z.object({
    placeId: GooglePlaceId,
    primaryText: z.string().min(1).max(300).openapi({ description: "Nombre o calle y numero; es la etiqueta que la app manda en el alta." }),
    secondaryText: z.string().min(1).max(300).optional().openapi({ description: "Localidad o referencia; ausente si Google no la da." }),
}).strict().openapi("PlaceSuggestion", { description: "Una sugerencia de la busqueda. Nunca trae coordenadas." });
export const PlaceAttribution = z.enum(["google"]).openapi("PlaceAttribution", {
    description: "Proveedor de las sugerencias. google: la lista lleva el logo de Google Maps al pie (ADR-020, R6).",
});
export const PlaceAutocompleteResponse = z.object({
    suggestions: z.array(PlaceSuggestion).max(PLACE_SUGGESTIONS_MAX).openapi({ description: "Hasta 5 sugerencias; vacia si no hay resultados." }),
    attribution: PlaceAttribution,
}).strict().openapi("PlaceAutocompleteResponse", { description: "Sugerencias sin coordenadas." });
//# sourceMappingURL=mobile-places.js.map